import React from 'react'

import {
  OrgMemberRole,
  OrgPermission,
  OrgRules,
  TeamMemberRole,
  TeamPermission,
  TeamRules,
} from '@makyoapp/makyo-sdk/models/ACL'
import { App16, BuildingAlt16, Customer16, Db16, Receipt16, Shield16, Team16 } from '@makyoapp/makyo-icon/icon'

export const OrgACLRoleKeys: OrgMemberRole[] = [
  OrgMemberRole.Admin,
  OrgMemberRole.Manager,
  OrgMemberRole.Accountant,
  OrgMemberRole.Member,
]

export const TeamACLRoleKeys: TeamMemberRole[] = [TeamMemberRole.Admin, TeamMemberRole.Editor, TeamMemberRole.Viewer]

export interface ACLRules {
  team: TeamRules
  org: OrgRules
}

export interface ACLValues<T extends string, R> {
  label: string
  icon: React.ReactNode
  checks: {
    [key in T]?: R[]
  }
}

export const buildOrgRoles = (perm: OrgPermission, acl: OrgRules): OrgMemberRole[] => {
  const data: OrgMemberRole[] = []

  OrgACLRoleKeys.forEach((v) => {
    if (acl[v][perm]) {
      data.push(v)
    }
  })

  return data
}

export const rebuildOrgRoles = (perm: OrgPermission, roles: OrgMemberRole[] = [], rules: OrgRules) => {
  OrgACLRoleKeys.forEach((role) => {
    rules[role][perm] = roles.includes(role)
  })

  // Owner case is always true
  rules[OrgMemberRole.Owner][perm] = true
}

export enum OrgACLLabel {
  // Org= index 0
  ProfileEdit = 'Edit Org Profile',
  Transfer = 'Transfer Organization',
  Remove = 'Remove Organization',
  // OrgDetailBilling= index 1
  BillingManagePaymentMethods = 'View & Manage Payment Methods',
  BillingManageSubscriptions = 'View & Manage Subscriptions',
  BillingManageAddresses = 'View & Manage Addresses',
  BillingViewActivities = 'View Transactions',
  // ACL= index 2
  ACLView = 'View',
  ACLMakeChanges = 'Make Changes',
  // Org Members= index 3
  MembersView = 'View List of Members',
  MembersInviteMembers = 'Invite Members',
  MembersEditMembers = 'Edit Members Role',
  MembersDeleteMembers = 'Delete Members (except for owner)',
  // Teams= index 4
  TeamsViewList = 'View List of Teams',
  TeamsCreate = 'Create New Teams',
  TeamsViewIndividualTeam = 'View Individual Team',
  //TeamsManageMembers = 'Manage Members (add/remove)',
  TeamsAddMembers = 'Add Members',
  TeamsDeleteMembers = 'Remove Members',
  TeamsChangeMemberRole = 'Edit Team Members Role',
  TeamsEditTeamInfo = 'Edit Team Info',
  TeamsDeleteTeams = 'Remove Teams',
  // Db= Index 5
  DBViewListOfDatabases = 'View List of Databases',
  DBCreateLinkDatabasesAndTables = 'Add Databases',
  DBAssignTeamsToDB = 'Actions Menu',
  // Apps= Index 6
  AppsViewListOfApps = 'View List of Apps',
  AppsCreateNewApp = 'Create New App',
  BrandKitAccess = 'Access Brand Kit',
  BrandKitEdit = 'Edit Brand Kit',
  AppsChangeTeams = 'Change Teams',
}

/**
 * when this acl is checked, the dependencies will also get checked
 * eq:
 *  - to create app you need to be able to list app
 *  - so when AppsCreateNewApp is checked
 *  - [AppsViewListOfApps, DBViewListOfDatabases] will also get checked
 * @param label
 */
export const getOrgACLRequiredDeps = (label: OrgACLLabel) => {
  switch (label) {
    case OrgACLLabel.ACLMakeChanges:
      return [OrgACLLabel.ACLView]
    case OrgACLLabel.MembersInviteMembers:
    case OrgACLLabel.MembersEditMembers:
    case OrgACLLabel.MembersDeleteMembers:
      return [OrgACLLabel.MembersView]
    case OrgACLLabel.TeamsCreate:
    case OrgACLLabel.TeamsViewIndividualTeam:
    case OrgACLLabel.TeamsAddMembers:
    case OrgACLLabel.TeamsDeleteMembers:
    case OrgACLLabel.TeamsChangeMemberRole:
    case OrgACLLabel.TeamsEditTeamInfo:
    case OrgACLLabel.TeamsDeleteTeams:
      return [OrgACLLabel.TeamsViewList]
    case OrgACLLabel.DBCreateLinkDatabasesAndTables:
    case OrgACLLabel.DBAssignTeamsToDB:
      return [OrgACLLabel.DBViewListOfDatabases]
    case OrgACLLabel.AppsCreateNewApp:
      return [OrgACLLabel.AppsViewListOfApps, [5, OrgACLLabel.DBViewListOfDatabases]]
    case OrgACLLabel.BrandKitEdit:
      return [OrgACLLabel.BrandKitAccess]
    default:
      return []
  }
}

/**
 * when this acl is unchecked, the dependencies will also get unchecked
 * eq:
 *  - when this acl is unchecked
 *  - check if it's of required dependencies
 *  - uncheck it when it's
 *  - when you uncheck AppsViewListOfApps
 *  - AppsCreateNewApp will also unchecked since its required dependencies is unchecked
 * @param label
 */
export const getOrgACLEffectedDeps = (label: OrgACLLabel) => {
  switch (label) {
    case OrgACLLabel.ACLView:
      return [OrgACLLabel.ACLMakeChanges]
    case OrgACLLabel.MembersView:
      return [OrgACLLabel.MembersInviteMembers, OrgACLLabel.MembersEditMembers, OrgACLLabel.MembersDeleteMembers]
    case OrgACLLabel.TeamsViewList:
      return [
        OrgACLLabel.TeamsCreate,
        OrgACLLabel.TeamsViewIndividualTeam,
        OrgACLLabel.TeamsAddMembers,
        OrgACLLabel.TeamsDeleteMembers,
        OrgACLLabel.TeamsChangeMemberRole,
        OrgACLLabel.TeamsEditTeamInfo,
        OrgACLLabel.TeamsDeleteTeams,
      ]
    case OrgACLLabel.DBViewListOfDatabases:
      return [
        OrgACLLabel.DBCreateLinkDatabasesAndTables,
        OrgACLLabel.DBAssignTeamsToDB,
        [6, OrgACLLabel.AppsCreateNewApp],
      ]
    case OrgACLLabel.BrandKitAccess:
      return [OrgACLLabel.BrandKitEdit]
    case OrgACLLabel.AppsViewListOfApps:
      return [OrgACLLabel.AppsCreateNewApp]
    default:
      return []
  }
}

export const getDefaultOrgACL = (role?: OrgMemberRole): OrgRules | Record<OrgPermission, boolean> => {
  const aclList: OrgRules = {
    [OrgMemberRole.Owner]: {
      [OrgPermission.ProfileEdit]: true,
      [OrgPermission.BillingManagePaymentMethods]: true,
      [OrgPermission.BillingManageSubscriptions]: true,
      [OrgPermission.BillingManageAddresses]: true,
      [OrgPermission.BillingViewActivities]: true,
      [OrgPermission.ACLView]: true,
      [OrgPermission.ACLMakeChanges]: true,
      [OrgPermission.MembersView]: true,
      [OrgPermission.MembersInviteMembers]: true,
      [OrgPermission.MembersEditMembers]: true,
      [OrgPermission.MembersDeleteMembers]: true,
      [OrgPermission.TeamsViewList]: true,
      [OrgPermission.TeamsCreate]: true,
      [OrgPermission.TeamsViewIndividualTeam]: true,
      [OrgPermission.TeamsAddMembers]: true,
      [OrgPermission.TeamsDeleteMembers]: true,
      [OrgPermission.TeamsChangeMemberRole]: true,
      [OrgPermission.TeamsEditTeamInfo]: true,
      [OrgPermission.TeamsDeleteTeams]: true,
      [OrgPermission.DBViewListOfDatabases]: true,
      [OrgPermission.DBCreateLinkDatabasesAndTables]: true,
      [OrgPermission.DBAssignTeamsToDB]: true,
      [OrgPermission.AppsViewListOfApps]: true,
      [OrgPermission.AppsCreateNewApp]: true,
      [OrgPermission.BrandKitAccess]: true,
      [OrgPermission.BrandKitEdit]: true,
      [OrgPermission.AppsChangeTeams]: true,
      [OrgPermission.Remove]: true,
      [OrgPermission.Transfer]: true,
    },
    [OrgMemberRole.Admin]: {
      [OrgPermission.ProfileEdit]: true,
      [OrgPermission.BillingManagePaymentMethods]: false,
      [OrgPermission.BillingManageSubscriptions]: false,
      [OrgPermission.BillingManageAddresses]: false,
      [OrgPermission.BillingViewActivities]: false,
      [OrgPermission.ACLView]: false,
      [OrgPermission.ACLMakeChanges]: false,
      [OrgPermission.MembersView]: true,
      [OrgPermission.MembersInviteMembers]: true,
      [OrgPermission.MembersEditMembers]: true,
      [OrgPermission.MembersDeleteMembers]: true,
      [OrgPermission.TeamsViewList]: true,
      [OrgPermission.TeamsCreate]: true,
      [OrgPermission.TeamsViewIndividualTeam]: true,
      [OrgPermission.TeamsAddMembers]: true,
      [OrgPermission.TeamsDeleteMembers]: true,
      [OrgPermission.TeamsChangeMemberRole]: true,
      [OrgPermission.TeamsEditTeamInfo]: true,
      [OrgPermission.TeamsDeleteTeams]: true,
      [OrgPermission.DBViewListOfDatabases]: true,
      [OrgPermission.DBCreateLinkDatabasesAndTables]: true,
      [OrgPermission.DBAssignTeamsToDB]: true,
      [OrgPermission.AppsViewListOfApps]: true,
      [OrgPermission.AppsCreateNewApp]: true,
      [OrgPermission.BrandKitAccess]: true,
      [OrgPermission.BrandKitEdit]: true,
      [OrgPermission.AppsChangeTeams]: true,
      [OrgPermission.Remove]: false,
      [OrgPermission.Transfer]: false,
    },
    [OrgMemberRole.Manager]: {
      [OrgPermission.ProfileEdit]: false,
      [OrgPermission.BillingManagePaymentMethods]: false,
      [OrgPermission.BillingManageSubscriptions]: false,
      [OrgPermission.BillingManageAddresses]: false,
      [OrgPermission.BillingViewActivities]: false,
      [OrgPermission.ACLView]: false,
      [OrgPermission.ACLMakeChanges]: false,
      [OrgPermission.MembersView]: false,
      [OrgPermission.MembersInviteMembers]: false,
      [OrgPermission.MembersEditMembers]: false,
      [OrgPermission.MembersDeleteMembers]: false,
      [OrgPermission.TeamsViewList]: true,
      [OrgPermission.TeamsCreate]: false,
      [OrgPermission.TeamsViewIndividualTeam]: true,
      [OrgPermission.TeamsAddMembers]: true,
      [OrgPermission.TeamsDeleteMembers]: true,
      [OrgPermission.TeamsChangeMemberRole]: true,
      [OrgPermission.TeamsEditTeamInfo]: true,
      [OrgPermission.TeamsDeleteTeams]: false,
      [OrgPermission.DBViewListOfDatabases]: false,
      [OrgPermission.DBCreateLinkDatabasesAndTables]: false,
      [OrgPermission.DBAssignTeamsToDB]: false,
      [OrgPermission.AppsViewListOfApps]: false,
      [OrgPermission.AppsCreateNewApp]: false,
      [OrgPermission.BrandKitAccess]: false,
      [OrgPermission.BrandKitEdit]: false,
      [OrgPermission.AppsChangeTeams]: false,
      [OrgPermission.Remove]: false,
      [OrgPermission.Transfer]: false,
    },
    [OrgMemberRole.Accountant]: {
      [OrgPermission.ProfileEdit]: false,
      [OrgPermission.BillingManagePaymentMethods]: true,
      [OrgPermission.BillingManageSubscriptions]: true,
      [OrgPermission.BillingManageAddresses]: true,
      [OrgPermission.BillingViewActivities]: true,
      [OrgPermission.ACLView]: false,
      [OrgPermission.ACLMakeChanges]: false,
      [OrgPermission.MembersView]: false,
      [OrgPermission.MembersInviteMembers]: false,
      [OrgPermission.MembersEditMembers]: false,
      [OrgPermission.MembersDeleteMembers]: false,
      [OrgPermission.TeamsViewList]: false,
      [OrgPermission.TeamsCreate]: false,
      [OrgPermission.TeamsViewIndividualTeam]: false,
      [OrgPermission.TeamsAddMembers]: false,
      [OrgPermission.TeamsDeleteMembers]: false,
      [OrgPermission.TeamsChangeMemberRole]: false,
      [OrgPermission.TeamsEditTeamInfo]: false,
      [OrgPermission.TeamsDeleteTeams]: false,
      [OrgPermission.DBViewListOfDatabases]: false,
      [OrgPermission.DBCreateLinkDatabasesAndTables]: false,
      [OrgPermission.DBAssignTeamsToDB]: false,
      [OrgPermission.AppsViewListOfApps]: false,
      [OrgPermission.AppsCreateNewApp]: false,
      [OrgPermission.BrandKitAccess]: false,
      [OrgPermission.BrandKitEdit]: false,
      [OrgPermission.AppsChangeTeams]: false,
      [OrgPermission.Remove]: false,
      [OrgPermission.Transfer]: false,
    },
    [OrgMemberRole.Member]: {
      [OrgPermission.ProfileEdit]: false,
      [OrgPermission.BillingManagePaymentMethods]: false,
      [OrgPermission.BillingManageSubscriptions]: false,
      [OrgPermission.BillingManageAddresses]: false,
      [OrgPermission.BillingViewActivities]: false,
      [OrgPermission.ACLView]: false,
      [OrgPermission.ACLMakeChanges]: false,
      [OrgPermission.MembersView]: false,
      [OrgPermission.MembersInviteMembers]: false,
      [OrgPermission.MembersEditMembers]: false,
      [OrgPermission.MembersDeleteMembers]: false,
      [OrgPermission.TeamsViewList]: false,
      [OrgPermission.TeamsCreate]: false,
      [OrgPermission.TeamsViewIndividualTeam]: false,
      [OrgPermission.TeamsAddMembers]: false,
      [OrgPermission.TeamsDeleteMembers]: false,
      [OrgPermission.TeamsChangeMemberRole]: false,
      [OrgPermission.TeamsEditTeamInfo]: true,
      [OrgPermission.TeamsDeleteTeams]: false,
      [OrgPermission.DBViewListOfDatabases]: false,
      [OrgPermission.DBCreateLinkDatabasesAndTables]: false,
      [OrgPermission.DBAssignTeamsToDB]: false,
      [OrgPermission.AppsViewListOfApps]: false,
      [OrgPermission.AppsCreateNewApp]: false,
      [OrgPermission.BrandKitAccess]: false,
      [OrgPermission.BrandKitEdit]: false,
      [OrgPermission.AppsChangeTeams]: false,
      [OrgPermission.Remove]: false,
      [OrgPermission.Transfer]: false,
    },
  }

  return role ? aclList[role] : aclList
}

export const getOrgAcl = (acl: OrgRules): ACLValues<OrgACLLabel, OrgMemberRole>[] => {
  return [
    {
      label: 'Org',
      icon: <BuildingAlt16 />,
      checks: {
        [OrgACLLabel.ProfileEdit]: buildOrgRoles(OrgPermission.ProfileEdit, acl),
        [OrgACLLabel.Transfer]: buildOrgRoles(OrgPermission.Transfer, acl),
        [OrgACLLabel.Remove]: buildOrgRoles(OrgPermission.Remove, acl),
      },
    },
    {
      label: 'Billing',
      icon: <Receipt16 />,
      checks: {
        [OrgACLLabel.BillingManagePaymentMethods]: buildOrgRoles(OrgPermission.BillingManagePaymentMethods, acl),
        [OrgACLLabel.BillingManageSubscriptions]: buildOrgRoles(OrgPermission.BillingManageSubscriptions, acl),
        [OrgACLLabel.BillingManageAddresses]: buildOrgRoles(OrgPermission.BillingManageAddresses, acl),
        [OrgACLLabel.BillingViewActivities]: buildOrgRoles(OrgPermission.BillingViewActivities, acl),
      },
    },
    {
      label: 'ACL',
      icon: <Shield16 />,
      checks: {
        [OrgACLLabel.ACLView]: buildOrgRoles(OrgPermission.ACLView, acl),
        [OrgACLLabel.ACLMakeChanges]: buildOrgRoles(OrgPermission.ACLMakeChanges, acl),
      },
    },
    {
      label: 'Org Members',
      icon: <Customer16 />,
      checks: {
        [OrgACLLabel.MembersView]: buildOrgRoles(OrgPermission.MembersView, acl),
        [OrgACLLabel.MembersInviteMembers]: buildOrgRoles(OrgPermission.MembersInviteMembers, acl),
        [OrgACLLabel.MembersEditMembers]: buildOrgRoles(OrgPermission.MembersEditMembers, acl),
        [OrgACLLabel.MembersDeleteMembers]: buildOrgRoles(OrgPermission.MembersDeleteMembers, acl),
      },
    },
    {
      label: 'Teams',
      icon: <Team16 />,
      checks: {
        [OrgACLLabel.TeamsViewList]: buildOrgRoles(OrgPermission.TeamsViewList, acl),
        [OrgACLLabel.TeamsCreate]: buildOrgRoles(OrgPermission.TeamsCreate, acl),
        [OrgACLLabel.TeamsViewIndividualTeam]: buildOrgRoles(OrgPermission.TeamsViewIndividualTeam, acl),
        [OrgACLLabel.TeamsAddMembers]: buildOrgRoles(OrgPermission.TeamsAddMembers, acl),
        [OrgACLLabel.TeamsDeleteMembers]: buildOrgRoles(OrgPermission.TeamsDeleteMembers, acl),
        [OrgACLLabel.TeamsChangeMemberRole]: buildOrgRoles(OrgPermission.TeamsChangeMemberRole, acl),
        [OrgACLLabel.TeamsEditTeamInfo]: buildOrgRoles(OrgPermission.TeamsEditTeamInfo, acl),
        [OrgACLLabel.TeamsDeleteTeams]: buildOrgRoles(OrgPermission.TeamsDeleteTeams, acl),
      },
    },
    {
      label: 'Database',
      icon: <Db16 />,
      checks: {
        [OrgACLLabel.DBViewListOfDatabases]: buildOrgRoles(OrgPermission.DBViewListOfDatabases, acl),
        [OrgACLLabel.DBCreateLinkDatabasesAndTables]: buildOrgRoles(OrgPermission.DBCreateLinkDatabasesAndTables, acl),
        [OrgACLLabel.DBAssignTeamsToDB]: buildOrgRoles(OrgPermission.DBAssignTeamsToDB, acl),
      },
    },
    {
      label: 'Apps',
      icon: <App16 />,
      checks: {
        [OrgACLLabel.AppsViewListOfApps]: buildOrgRoles(OrgPermission.AppsViewListOfApps, acl),
        [OrgACLLabel.AppsCreateNewApp]: buildOrgRoles(OrgPermission.AppsCreateNewApp, acl),
        [OrgACLLabel.AppsChangeTeams]: buildOrgRoles(OrgPermission.AppsChangeTeams, acl),
      },
    },
    {
      label: 'Brand Kit',
      icon: <App16 />,
      checks: {
        [OrgACLLabel.BrandKitAccess]: buildOrgRoles(OrgPermission.BrandKitAccess, acl),
        [OrgACLLabel.BrandKitEdit]: buildOrgRoles(OrgPermission.BrandKitEdit, acl),
      },
    },
  ]
}

export const rebuildOrgACL = (aclValues: ACLValues<OrgACLLabel, OrgMemberRole>[], rules: OrgRules): OrgRules => {
  const acl: OrgRules = { ...rules }

  // Org, index 0
  rebuildOrgRoles(OrgPermission.ProfileEdit, aclValues[0].checks[OrgACLLabel.ProfileEdit], acl)
  rebuildOrgRoles(OrgPermission.Transfer, aclValues[0].checks[OrgACLLabel.Transfer], acl)
  rebuildOrgRoles(OrgPermission.Remove, aclValues[0].checks[OrgACLLabel.Remove], acl)

  // OrgDetailBilling, index 1
  rebuildOrgRoles(
    OrgPermission.BillingManagePaymentMethods,
    aclValues[1].checks[OrgACLLabel.BillingManagePaymentMethods],
    acl
  )
  rebuildOrgRoles(
    OrgPermission.BillingManageSubscriptions,
    aclValues[1].checks[OrgACLLabel.BillingManageSubscriptions],
    acl
  )
  rebuildOrgRoles(OrgPermission.BillingManageAddresses, aclValues[1].checks[OrgACLLabel.BillingManageAddresses], acl)
  rebuildOrgRoles(OrgPermission.BillingViewActivities, aclValues[1].checks[OrgACLLabel.BillingViewActivities], acl)

  // ACL, index 2
  rebuildOrgRoles(OrgPermission.ACLView, aclValues[2].checks[OrgACLLabel.ACLView], acl)
  rebuildOrgRoles(OrgPermission.ACLMakeChanges, aclValues[2].checks[OrgACLLabel.ACLMakeChanges], acl)

  // Org Members, index 3
  rebuildOrgRoles(OrgPermission.MembersView, aclValues[3].checks[OrgACLLabel.MembersView], acl)
  rebuildOrgRoles(OrgPermission.MembersInviteMembers, aclValues[3].checks[OrgACLLabel.MembersInviteMembers], acl)
  rebuildOrgRoles(OrgPermission.MembersEditMembers, aclValues[3].checks[OrgACLLabel.MembersEditMembers], acl)
  rebuildOrgRoles(OrgPermission.MembersDeleteMembers, aclValues[3].checks[OrgACLLabel.MembersDeleteMembers], acl)

  // Teams, index 4
  rebuildOrgRoles(OrgPermission.TeamsViewList, aclValues[4].checks[OrgACLLabel.TeamsViewList], acl)
  rebuildOrgRoles(OrgPermission.TeamsCreate, aclValues[4].checks[OrgACLLabel.TeamsCreate], acl)
  rebuildOrgRoles(OrgPermission.TeamsViewIndividualTeam, aclValues[4].checks[OrgACLLabel.TeamsViewIndividualTeam], acl)
  rebuildOrgRoles(OrgPermission.TeamsAddMembers, aclValues[4].checks[OrgACLLabel.TeamsAddMembers], acl)
  rebuildOrgRoles(OrgPermission.TeamsDeleteMembers, aclValues[4].checks[OrgACLLabel.TeamsDeleteMembers], acl)
  rebuildOrgRoles(OrgPermission.TeamsChangeMemberRole, aclValues[4].checks[OrgACLLabel.TeamsChangeMemberRole], acl)
  rebuildOrgRoles(OrgPermission.TeamsEditTeamInfo, aclValues[4].checks[OrgACLLabel.TeamsEditTeamInfo], acl)
  rebuildOrgRoles(OrgPermission.TeamsDeleteTeams, aclValues[4].checks[OrgACLLabel.TeamsDeleteTeams], acl)

  // Db, Index 5
  rebuildOrgRoles(OrgPermission.DBViewListOfDatabases, aclValues[5].checks[OrgACLLabel.DBViewListOfDatabases], acl)
  rebuildOrgRoles(
    OrgPermission.DBCreateLinkDatabasesAndTables,
    aclValues[5].checks[OrgACLLabel.DBCreateLinkDatabasesAndTables],
    acl
  )
  rebuildOrgRoles(OrgPermission.DBAssignTeamsToDB, aclValues[5].checks[OrgACLLabel.DBAssignTeamsToDB], acl)

  // Apps, Index 6
  rebuildOrgRoles(OrgPermission.AppsViewListOfApps, aclValues[6].checks[OrgACLLabel.AppsViewListOfApps], acl)
  rebuildOrgRoles(OrgPermission.AppsCreateNewApp, aclValues[6].checks[OrgACLLabel.AppsCreateNewApp], acl)
  rebuildOrgRoles(OrgPermission.AppsChangeTeams, aclValues[6].checks[OrgACLLabel.AppsChangeTeams], acl)

  // Apps, Index 7
  rebuildOrgRoles(OrgPermission.BrandKitAccess, aclValues[7].checks[OrgACLLabel.BrandKitAccess], acl)
  rebuildOrgRoles(OrgPermission.BrandKitEdit, aclValues[7].checks[OrgACLLabel.BrandKitEdit], acl)
  return acl
}

export const buildTeamRoles = (perm: TeamPermission, rules: TeamRules): TeamMemberRole[] => {
  const data: TeamMemberRole[] = []

  TeamACLRoleKeys.forEach((v) => {
    if (rules[v][perm]) {
      data.push(v)
    }
  })

  return data
}

export const rebuildTeamRoles = (perm: TeamPermission, roles: TeamMemberRole[] = [], rules: TeamRules) => {
  TeamACLRoleKeys.forEach((role) => {
    rules[role][perm] = roles.includes(role)
  })

  // Owner case is always true
  //rules[TeamMemberRole.Admin][perm] = true
}

export enum TeamACLLabel {
  // DB, index 0
  DBViewIndividualDatabasesTablesRecords = 'View Individual DB & Table Records',
  DBEditDatabasesAndTables = 'Edit DB & Tables',
  DBDeleteDatabasesAndTables = 'Delete DB & Tables',
  DBEditRecords = 'Edit Records',
  DBDeleteRecords = 'Delete Records',
  DBEditColumns = 'Edit Columns',
  DBDeleteColumns = 'Delete Columns',

  // APPS, index 1
  AppsAppBuilder = 'App Builder',
  AppsViewAppData = 'View App Data',
  AppsLinkDatabases = 'Link DB',
  AppsChangePlan = 'Change Plan',
  AppsEditAppInfo = 'Edit App Info',
  AppsChangeDomain = 'Change Domain',
  AppsManagePlugins = 'Manage Plugins',
  AppsMenuStyleManagers = 'Managers',
  AppsPrivacy = 'Privacy',
  AppsDelete = 'Delete',
  AppsViewIndividualApps = 'View Individual App',
  AppsPublishUnPublish = 'Apps Publish/Un-publish',
}

export const getTeamACLRequiredDeps = (label: TeamACLLabel) => {
  switch (label) {
    case TeamACLLabel.DBDeleteColumns:
    case TeamACLLabel.DBDeleteDatabasesAndTables:
    default:
      return []
  }
}

export const getTeamACLEffectedDeps = (label: TeamACLLabel) => {
  switch (label) {
    case TeamACLLabel.DBViewIndividualDatabasesTablesRecords:
      return []
    default:
      return []
  }
}

export const getDefaultTeamACL = (role?: TeamMemberRole): TeamRules | Record<TeamPermission, boolean> => {
  const aclList: TeamRules = {
    [TeamMemberRole.Admin]: {
      [TeamPermission.DBViewIndividualDatabasesTablesRecords]: true,
      [TeamPermission.DBEditDatabasesAndTables]: true,
      [TeamPermission.DBDeleteDatabasesAndTables]: true,
      [TeamPermission.DBEditRecords]: true,
      [TeamPermission.DBDeleteRecords]: true,
      [TeamPermission.DBEditColumns]: true,
      [TeamPermission.DBDeleteColumns]: true,
      [TeamPermission.AppsAppBuilder]: true,
      [TeamPermission.AppsViewAppData]: true,
      [TeamPermission.AppsLinkDatabases]: true,
      [TeamPermission.AppsChangePlan]: true,
      [TeamPermission.AppsPublishUnpublish]: true,
      [TeamPermission.AppsEditAppInfo]: true,
      [TeamPermission.AppsChangeDomain]: true,
      [TeamPermission.AppsManagePlugins]: true,
      [TeamPermission.AppsMenuStyleManagers]: true,
      [TeamPermission.AppsPrivacy]: true,
      [TeamPermission.AppsDelete]: true,
      [TeamPermission.AppsViewIndividualApps]: true,
    },
    [TeamMemberRole.Editor]: {
      [TeamPermission.DBViewIndividualDatabasesTablesRecords]: true,
      [TeamPermission.DBEditDatabasesAndTables]: true,
      [TeamPermission.DBDeleteDatabasesAndTables]: false,
      [TeamPermission.DBEditRecords]: true,
      [TeamPermission.DBDeleteRecords]: true,
      [TeamPermission.DBEditColumns]: true,
      [TeamPermission.DBDeleteColumns]: true,
      [TeamPermission.AppsAppBuilder]: true,
      [TeamPermission.AppsViewAppData]: true,
      [TeamPermission.AppsLinkDatabases]: true,
      [TeamPermission.AppsChangePlan]: false,
      [TeamPermission.AppsPublishUnpublish]: false,
      [TeamPermission.AppsEditAppInfo]: true,
      [TeamPermission.AppsChangeDomain]: false,
      [TeamPermission.AppsManagePlugins]: false,
      [TeamPermission.AppsMenuStyleManagers]: true,
      [TeamPermission.AppsPrivacy]: false,
      [TeamPermission.AppsDelete]: false,
      [TeamPermission.AppsViewIndividualApps]: false,
    },
    [TeamMemberRole.Viewer]: {
      [TeamPermission.DBViewIndividualDatabasesTablesRecords]: true,
      [TeamPermission.DBEditDatabasesAndTables]: false,
      [TeamPermission.DBDeleteDatabasesAndTables]: false,
      [TeamPermission.DBEditRecords]: false,
      [TeamPermission.DBDeleteRecords]: false,
      [TeamPermission.DBEditColumns]: false,
      [TeamPermission.DBDeleteColumns]: false,
      [TeamPermission.AppsAppBuilder]: true,
      [TeamPermission.AppsViewAppData]: true,
      [TeamPermission.AppsLinkDatabases]: false,
      [TeamPermission.AppsChangePlan]: false,
      [TeamPermission.AppsPublishUnpublish]: false,
      [TeamPermission.AppsEditAppInfo]: false,
      [TeamPermission.AppsChangeDomain]: false,
      [TeamPermission.AppsManagePlugins]: false,
      [TeamPermission.AppsMenuStyleManagers]: false,
      [TeamPermission.AppsPrivacy]: false,
      [TeamPermission.AppsDelete]: false,
      [TeamPermission.AppsViewIndividualApps]: false,
    },
  }

  return role ? aclList[role] : aclList
}

export const getTeamACL = (acl: TeamRules): ACLValues<TeamACLLabel, TeamMemberRole>[] => {
  return [
    {
      label: 'Database',
      icon: <Db16 />,
      checks: {
        [TeamACLLabel.DBViewIndividualDatabasesTablesRecords]: buildTeamRoles(
          TeamPermission.DBViewIndividualDatabasesTablesRecords,
          acl
        ),
        [TeamACLLabel.DBEditDatabasesAndTables]: buildTeamRoles(TeamPermission.DBEditDatabasesAndTables, acl),
        [TeamACLLabel.DBDeleteDatabasesAndTables]: buildTeamRoles(TeamPermission.DBDeleteDatabasesAndTables, acl),
        [TeamACLLabel.DBEditRecords]: buildTeamRoles(TeamPermission.DBEditRecords, acl),
        [TeamACLLabel.DBDeleteRecords]: buildTeamRoles(TeamPermission.DBDeleteRecords, acl),
        [TeamACLLabel.DBEditColumns]: buildTeamRoles(TeamPermission.DBEditColumns, acl),
        [TeamACLLabel.DBDeleteColumns]: buildTeamRoles(TeamPermission.DBDeleteColumns, acl),
      },
    },
    {
      label: 'Apps',
      icon: <App16 />,
      checks: {
        [TeamACLLabel.AppsAppBuilder]: buildTeamRoles(TeamPermission.AppsAppBuilder, acl),
        [TeamACLLabel.AppsViewAppData]: buildTeamRoles(TeamPermission.AppsViewAppData, acl),
        [TeamACLLabel.AppsLinkDatabases]: buildTeamRoles(TeamPermission.AppsLinkDatabases, acl),
        [TeamACLLabel.AppsChangePlan]: buildTeamRoles(TeamPermission.AppsChangePlan, acl),
        [TeamACLLabel.AppsPublishUnPublish]: buildTeamRoles(TeamPermission.AppsPublishUnpublish, acl),
        [TeamACLLabel.AppsEditAppInfo]: buildTeamRoles(TeamPermission.AppsEditAppInfo, acl),
        [TeamACLLabel.AppsChangeDomain]: buildTeamRoles(TeamPermission.AppsChangeDomain, acl),
        [TeamACLLabel.AppsManagePlugins]: buildTeamRoles(TeamPermission.AppsManagePlugins, acl),
        [TeamACLLabel.AppsMenuStyleManagers]: buildTeamRoles(TeamPermission.AppsMenuStyleManagers, acl),
        [TeamACLLabel.AppsPrivacy]: buildTeamRoles(TeamPermission.AppsPrivacy, acl),
        [TeamACLLabel.AppsDelete]: buildTeamRoles(TeamPermission.AppsDelete, acl),
        [TeamACLLabel.AppsViewIndividualApps]: buildTeamRoles(TeamPermission.AppsViewIndividualApps, acl),
      },
    },
  ]
}

export const rebuildTeamACL = (aclValues: ACLValues<TeamACLLabel, TeamMemberRole>[], rules: TeamRules): TeamRules => {
  const acl = { ...rules }

  // DB, index 0
  rebuildTeamRoles(
    TeamPermission.DBViewIndividualDatabasesTablesRecords,
    aclValues[0].checks[TeamACLLabel.DBViewIndividualDatabasesTablesRecords],
    acl
  )

  rebuildTeamRoles(
    TeamPermission.DBEditDatabasesAndTables,
    aclValues[0].checks[TeamACLLabel.DBEditDatabasesAndTables],
    acl
  )

  rebuildTeamRoles(
    TeamPermission.DBDeleteDatabasesAndTables,
    aclValues[0].checks[TeamACLLabel.DBDeleteDatabasesAndTables],
    acl
  )
  rebuildTeamRoles(TeamPermission.DBEditRecords, aclValues[0].checks[TeamACLLabel.DBEditRecords], acl)
  rebuildTeamRoles(TeamPermission.DBDeleteRecords, aclValues[0].checks[TeamACLLabel.DBDeleteRecords], acl)
  rebuildTeamRoles(TeamPermission.DBEditColumns, aclValues[0].checks[TeamACLLabel.DBEditColumns], acl)
  rebuildTeamRoles(TeamPermission.DBDeleteColumns, aclValues[0].checks[TeamACLLabel.DBDeleteColumns], acl)

  // APPS, index 1
  rebuildTeamRoles(TeamPermission.AppsAppBuilder, aclValues[1].checks[TeamACLLabel.AppsAppBuilder], acl)
  rebuildTeamRoles(TeamPermission.AppsViewAppData, aclValues[1].checks[TeamACLLabel.AppsViewAppData], acl)
  rebuildTeamRoles(TeamPermission.AppsLinkDatabases, aclValues[1].checks[TeamACLLabel.AppsLinkDatabases], acl)
  rebuildTeamRoles(TeamPermission.AppsChangePlan, aclValues[1].checks[TeamACLLabel.AppsChangePlan], acl)
  rebuildTeamRoles(TeamPermission.AppsPublishUnpublish, aclValues[1].checks[TeamACLLabel.AppsPublishUnPublish], acl)
  rebuildTeamRoles(TeamPermission.AppsEditAppInfo, aclValues[1].checks[TeamACLLabel.AppsEditAppInfo], acl)
  rebuildTeamRoles(TeamPermission.AppsChangeDomain, aclValues[1].checks[TeamACLLabel.AppsChangeDomain], acl)
  rebuildTeamRoles(TeamPermission.AppsManagePlugins, aclValues[1].checks[TeamACLLabel.AppsManagePlugins], acl)
  rebuildTeamRoles(TeamPermission.AppsMenuStyleManagers, aclValues[1].checks[TeamACLLabel.AppsMenuStyleManagers], acl)
  rebuildTeamRoles(TeamPermission.AppsPrivacy, aclValues[1].checks[TeamACLLabel.AppsPrivacy], acl)
  rebuildTeamRoles(TeamPermission.AppsDelete, aclValues[1].checks[TeamACLLabel.AppsDelete], acl)
  rebuildTeamRoles(TeamPermission.AppsViewIndividualApps, aclValues[1].checks[TeamACLLabel.AppsViewIndividualApps], acl)

  return acl
}
